interface AnalyticsConfig {
    enabled: boolean;
    type: 'google_analytics' | 'google_tag_manager';
    googleAnalytics?: {
        measurementId: string;
    };
    googleTagManager?: {
        containerId: string;
    };
}

class AnalyticsService {
    private static instance: AnalyticsService;
    private initialized: boolean = false;
    private config: AnalyticsConfig | null = null;

    private constructor() {}

    static getInstance(): AnalyticsService {
        if (!AnalyticsService.instance) {
            AnalyticsService.instance = new AnalyticsService();
        }
        return AnalyticsService.instance;
    }

    initialize(config: AnalyticsConfig) {
        if (!config.enabled) {
            this.removeScripts();
            return;
        }

        this.config = config;

        if (config.type === 'google_analytics' && config.googleAnalytics?.measurementId) {
            this.initializeGA4(config.googleAnalytics.measurementId);
        } else if (config.type === 'google_tag_manager' && config.googleTagManager?.containerId) {
            this.initializeGTM(config.googleTagManager.containerId);
        }

        this.initialized = true;
    }

    private initializeGA4(measurementId: string) {
        // Remove any existing GA scripts
        this.removeScripts();

        // Add GA4 script
        const script = document.createElement('script');
        script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
        script.async = true;
        script.id = 'ga-script';
        document.head.appendChild(script);

        // Initialize gtag
        window.dataLayer = window.dataLayer || [];
        function gtag(...args: any[]) {
            window.dataLayer?.push(arguments);
        }
        window.gtag = gtag;
        gtag('js', new Date());
        gtag('config', measurementId, {
            send_page_view: true
        });
    }

    private initializeGTM(containerId: string) {
        // Remove any existing scripts
        this.removeScripts();

        // Add GTM script
        const script = document.createElement('script');
        script.innerHTML = `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${containerId}');
        `;
        script.id = 'gtm-script';
        document.head.appendChild(script);

        // Add GTM noscript iframe
        const noscript = document.createElement('noscript');
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
        iframe.height = '0';
        iframe.width = '0';
        iframe.style.display = 'none';
        iframe.style.visibility = 'hidden';
        noscript.appendChild(iframe);
        document.body.appendChild(noscript);
    }

    private removeScripts() {
        // Remove GA script if exists
        const gaScript = document.getElementById('ga-script');
        if (gaScript) {
            gaScript.remove();
        }

        // Remove GTM script if exists
        const gtmScript = document.getElementById('gtm-script');
        if (gtmScript) {
            gtmScript.remove();
        }

        // Remove GTM noscript iframe
        const iframes = document.getElementsByTagName('iframe');
        for (let i = iframes.length - 1; i >= 0; i--) {
            const iframe = iframes[i];
            if (iframe.src.includes('googletagmanager.com/ns.html')) {
                iframe.remove();
            }
        }

        // Reset global variables
        delete window.dataLayer;
        delete window.gtag;
    }

    // Track page view
    trackPageView(path: string, title?: string) {
        if (!this.initialized || !this.config?.enabled) return;

        if (this.config.type === 'google_analytics' && window.gtag) {
            window.gtag('event', 'page_view', {
                page_path: path,
                page_title: title
            });
        } else if (this.config.type === 'google_tag_manager' && window.dataLayer) {
            window.dataLayer.push({
                event: 'page_view',
                page_path: path,
                page_title: title
            });
        }
    }

    // Track event
    trackEvent(category: string, action: string, label?: string, value?: number) {
        if (!this.initialized || !this.config?.enabled) return;

        if (this.config.type === 'google_analytics' && window.gtag) {
            window.gtag('event', action, {
                event_category: category,
                event_label: label,
                value: value
            });
        } else if (this.config.type === 'google_tag_manager' && window.dataLayer) {
            window.dataLayer.push({
                event: action,
                event_category: category,
                event_label: label,
                event_value: value
            });
        }
    }
}

// Add types for window object
declare global {
    interface Window {
        dataLayer?: any[];
        gtag?: (...args: any[]) => void;
    }
}

export const analyticsService = AnalyticsService.getInstance();
